import { FC, useState } from 'react';
import {
  Bold,
  CandidateButtonLink,
  CandidatesColumn,
  CandidatesColumns,
  CandidatesFooter,
  CandidatesGrid,
  CandidatesSection,
} from '../styled';
import { EditCandidatesModal } from './EditCandidatesModal';
import { CandidateItem } from './CandidateItem';
import ButtonLink from 'components/ButtonLink';
import { IRecipient } from 'store/recipients/types';
import { UtilityText } from 'components/Typography';
import { getUrlParams } from 'helpers/utils';
import { useAppSelector } from 'helpers/hooks';
import { getContributionSummariesByRecipientId } from 'selectors/contributionSummary';
import classNames from 'classnames';
import { IAllocationType } from 'store/donatePage/types';

const MAX_CANDIDATES_PER_PAGE_DESKTOP = 12;
const MAX_CANDIDATES_PER_PAGE_MOBILE = 4;

const getNumSelectedCandidates = (candidates: CandidateType[]) =>
  candidates.filter(candidate => candidate.isSelected).length;

export interface CandidateType extends IRecipient {
  donationAmount?: number;
  index: number;
  isSelected: boolean;
}

interface CandidatesProps {
  allocationType: IAllocationType;
  candidates: CandidateType[];
  setCandidates: (updatedCandidates: CandidateType[]) => void;
  recipientCount: number;
}

export const Candidates: FC<CandidatesProps> = ({
  allocationType,
  candidates,
  recipientCount,
  setCandidates,
}) => {
  const { urlPartner } = getUrlParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Donor's cycle giving keyed by recipient_id. Populated by the summaries fetch
  // in Main/index; here we only read it to show the "Donated" pill per recipient.
  const contributionSummaries = useAppSelector(getContributionSummariesByRecipientId);
  const numSelectedCandidates = getNumSelectedCandidates(candidates);
  const hasOneRecipientOnly = recipientCount === 1;

  const onEditCandidatesButtonClick = () => {
    setIsEditModalOpen(true);
  };

  // Sort candidates to have selected ones at the top
  const sortedCandidates = [...candidates].sort((a, b) => {
    if (a.isSelected === b.isSelected) {
      return 0;
    }
    return a.isSelected ? -1 : 1;
  });

  return (
    <>
      {allocationType === 'WATERFALL' || allocationType === 'WEIGHTED' ? (
        <div>
          <div className="body-text">{`Your contribution is a ${allocationType.toLowerCase()} split`}</div>
          <div className="utility-text">
            The split will be based according to your organization's allocation guidelines.
          </div>
        </div>
      ) : (
        <>
          <div>
            <span>
              Your contribution will{' '}
              {numSelectedCandidates === 1 ? 'go to' : 'be split between'}{' '}
            </span>
            <Bold>
              {hasOneRecipientOnly && candidates.length > 0
                ? candidates[0].recipient_name
                : `${numSelectedCandidates} recipient${numSelectedCandidates !== 1 ? 's' : ''}.`}
            </Bold>
          </div>
          {numSelectedCandidates > 1 && (
            <div style={{ marginTop: '-4px' }}>
              <UtilityText>The split will be even unless customized below.</UtilityText>
            </div>
          )}
        </>
      )}
      {allocationType === 'EVEN' && (
        <CandidatesSection>
          {recipientCount > 1 && (
            <div>
              {`${getNumSelectedCandidates(candidates)} out of ${
                candidates.length
              } recipients selected:`}
            </div>
          )}
          <CandidatesGrid
            className={classNames({ 'single-recipient': hasOneRecipientOnly })}
            $hideForSmall={true}
          >
            {sortedCandidates.slice(0, MAX_CANDIDATES_PER_PAGE_DESKTOP).map(candidate => (
              <CandidateItem
                key={candidate.de_id}
                candidate={candidate}
                hasOneRecipientOnly={hasOneRecipientOnly}
                summary={contributionSummaries[candidate.recipient_id]}
                urlPartner={urlPartner}
              />
            ))}
          </CandidatesGrid>
          <CandidatesColumns $hideForLarge={true}>
            <CandidatesColumn>
              {sortedCandidates.slice(0, MAX_CANDIDATES_PER_PAGE_MOBILE).map(candidate => (
                <CandidateItem
                  key={candidate.de_id}
                  candidate={candidate}
                  hasOneRecipientOnly={hasOneRecipientOnly}
                  summary={contributionSummaries[candidate.recipient_id]}
                  urlPartner={urlPartner}
                />
              ))}
            </CandidatesColumn>
          </CandidatesColumns>
          {!hasOneRecipientOnly && (
            <CandidatesFooter>
              <CandidateButtonLink
                $numCandidates={candidates.length}
                onClick={onEditCandidatesButtonClick}
              >
                See all {candidates.length} recipients
              </CandidateButtonLink>
              <ButtonLink onClick={onEditCandidatesButtonClick}>Edit recipients</ButtonLink>
            </CandidatesFooter>
          )}
        </CandidatesSection>
      )}
      <EditCandidatesModal
        candidates={candidates}
        isOpen={isEditModalOpen}
        setCandidates={setCandidates}
        setIsOpen={setIsEditModalOpen}
      />
    </>
  );
};
